import React, { useState } from "react";
import { Upload, X, Loader2, Video, Play } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ReturnBadge, RETURN_REASONS, RETURN_STATUS } from "@/lib/returns";

export function MediaUploader({ files, setFiles, accept = "image/*", kind = "image", max = 5, testid }) {
  const [uploading, setUploading] = useState(false);
  const onFiles = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    if (files.length + list.length > max) { toast.error(`الحد الأقصى ${max}`); return; }
    setUploading(true);
    try {
      for (const file of list) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setFiles((prev) => [...prev, data.path]);
      }
      toast.success("تم الرفع");
    } catch { toast.error("فشل الرفع"); }
    finally { setUploading(false); e.target.value = ""; }
  };
  return (
    <div className="flex flex-wrap gap-3">
      {files.map((f, i) => (
        <div key={i} className="relative h-20 w-20 rounded-xl overflow-hidden border" style={{ borderColor: "var(--border-c)" }}>
          {kind === "video"
            ? <div className="flex h-full w-full items-center justify-center bg-[var(--bg-secondary)] text-[var(--amber)]"><Play size={22} /></div>
            : <img src={fileUrl(f)} alt="" className="h-full w-full object-cover" />}
          <button type="button" onClick={() => setFiles(files.filter((_, x) => x !== i))} className="absolute top-1 left-1 rounded-full bg-black/70 p-1 text-white"><X size={12} /></button>
        </div>
      ))}
      {files.length < max && (
        <label data-testid={testid} className="h-20 w-20 rounded-xl border-2 border-dashed flex items-center justify-center cursor-pointer text-[var(--text-muted)] hover:border-[var(--amber)] hover:text-[var(--amber)]" style={{ borderColor: "var(--border-c)" }}>
          {uploading ? <Loader2 size={22} className="animate-spin" /> : (kind === "video" ? <Video size={22} /> : <Upload size={22} />)}
          <input type="file" accept={accept} multiple hidden onChange={onFiles} />
        </label>
      )}
    </div>
  );
}

export function CustomerReturnDialog({ order, onClose, onDone }) {
  const [type, setType] = useState("return");
  const [reason, setReason] = useState("");
  const [description, setDescription] = useState("");
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!reason) { toast.error("اختر سبب الطلب"); return; }
    if (!description.trim()) { toast.error("اشرح المشكلة"); return; }
    setLoading(true);
    try {
      await api.post("/returns", { order_id: order.id, request_type: type, reason_code: reason, description, images, videos });
      toast.success("تم إرسال طلبك — قيد المراجعة");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-[var(--bg-card)] border-[var(--border-c)] text-white max-w-md max-h-[90vh] overflow-y-auto" dir="rtl">
        <DialogHeader><DialogTitle className="text-white text-right">طلب استرجاع / استبدال</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-2">
            {[["return", "استرجاع"], ["exchange", "استبدال"]].map(([v, l]) => (
              <button key={v} data-testid={`return-type-${v}`} onClick={() => setType(v)}
                className={`rounded-xl py-2.5 text-sm font-bold border ${type === v ? "border-[var(--amber)] text-[var(--amber)] bg-[var(--amber)]/10" : "border-[var(--border-c)] text-[var(--text-muted)]"}`}>{l}</button>
            ))}
          </div>
          <div>
            <label className="text-xs font-bold text-[var(--text-muted)] block mb-1.5">سبب الطلب</label>
            <select data-testid="return-reason" value={reason} onChange={(e) => setReason(e.target.value)} className="w-full rounded-xl bg-[var(--bg-secondary)] px-3 py-2.5 outline-none">
              <option value="">اختر السبب</option>
              {RETURN_REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-bold text-[var(--text-muted)] block mb-1.5">اشرح المشكلة</label>
            <textarea data-testid="return-description" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="w-full rounded-xl bg-[var(--bg-secondary)] px-3 py-2.5 outline-none resize-none" />
          </div>
          <div>
            <label className="text-xs font-bold text-[var(--text-muted)] block mb-1.5">إرفاق صور</label>
            <MediaUploader files={images} setFiles={setImages} accept="image/*" kind="image" testid="return-images-upload" />
          </div>
          <div>
            <label className="text-xs font-bold text-[var(--text-muted)] block mb-1.5">إرفاق فيديو</label>
            <MediaUploader files={videos} setFiles={setVideos} accept="video/*" kind="video" max={2} testid="return-videos-upload" />
          </div>
          <button data-testid="submit-return-request" onClick={submit} disabled={loading} className="btn-amber w-full justify-center disabled:opacity-60">
            {loading ? "جارٍ الإرسال..." : "إرسال الطلب"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const SELLER_STATUSES = ["under_review", "awaiting_customer_info", "approved", "rejected", "awaiting_pickup", "item_received", "exchanging", "exchanged", "refunding", "refunded"];
const ADMIN_STATUSES = ["submitted", "under_review", "awaiting_yard", "awaiting_customer_info", "approved", "rejected", "awaiting_pickup", "item_received", "exchanging", "exchanged", "refunding", "refunded", "closed"];

export function ReturnCard({ req, role, onUpdated }) {
  const [status, setStatus] = useState("");
  const [note, setNote] = useState("");
  const [open, setOpen] = useState(false);

  const update = async (st) => {
    const target = st || status;
    if (!target) { toast.error("اختر الحالة"); return; }
    if (target === "rejected" && !note.trim()) { toast.error("اذكر سبب الرفض"); return; }
    try {
      await api.put(`/returns/${req.id}/status`, { status: target, note });
      toast.success("تم تحديث الحالة"); setNote(""); setStatus(""); onUpdated && onUpdated();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const options = role === "admin" ? ADMIN_STATUSES : SELLER_STATUSES;

  return (
    <div className="ts-card p-4" data-testid={`return-card-${req.id}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-[var(--text-muted)]">{req.request_number} · طلب #{req.order_number}</span>
        <ReturnBadge status={req.status} />
      </div>
      <div className="flex gap-3">
        {req.part_snapshot?.image && <img src={fileUrl(req.part_snapshot.image)} alt="" className="h-14 w-14 rounded-lg object-cover" />}
        <div className="flex-1 min-w-0">
          <p className="font-bold text-white text-sm">{req.part_snapshot?.name}</p>
          <p className="text-xs text-[var(--amber)]">{req.request_type === "exchange" ? "استبدال" : "استرجاع"} · {req.reason_label}</p>
          {(role === "seller" || role === "admin") && <p className="text-xs text-[var(--text-muted)]">العميل: {req.customer_name}</p>}
          {role === "admin" && <p className="text-xs text-[var(--text-muted)]">التشليح: {req.yard_name}</p>}
        </div>
      </div>
      <p className="text-sm text-[var(--text-muted)] mt-2">{req.description}</p>

      {(req.images?.length > 0 || req.videos?.length > 0) && (
        <div className="flex flex-wrap gap-2 mt-3">
          {req.images?.map((im, i) => <a key={i} href={fileUrl(im)} target="_blank" rel="noreferrer"><img src={fileUrl(im)} alt="" className="h-16 w-16 rounded-lg object-cover" /></a>)}
          {req.videos?.map((v, i) => <a key={i} href={fileUrl(v)} target="_blank" rel="noreferrer" data-testid={`return-video-${i}`} className="h-16 w-16 rounded-lg bg-[var(--bg-secondary)] flex items-center justify-center text-[var(--amber)]"><Play size={20} /></a>)}
        </div>
      )}

      {req.yard_response && <p className="text-xs text-[var(--text-muted)] mt-2 border-r-2 pr-2" style={{ borderColor: "var(--amber)" }}>رد التشليح: {req.yard_response}</p>}
      {role === "admin" && req.admin_notes && <p className="text-xs text-[var(--text-subtle)] mt-1 whitespace-pre-line">ملاحظات الإدارة: {req.admin_notes}</p>}

      <button onClick={() => setOpen(!open)} className="text-xs text-[var(--amber)] mt-3">{open ? "إخفاء" : "عرض"} سجل الحالات ({req.status_history?.length || 0})</button>
      {open && (
        <div className="mt-2 space-y-1">
          {req.status_history?.map((h, i) => (
            <div key={i} className="text-[11px] text-[var(--text-muted)] flex items-center justify-between">
              <span>{RETURN_STATUS[h.status]?.label || h.status}{h.note ? ` — ${h.note}` : ""}</span>
              <span className="text-[var(--text-subtle)]">{new Date(h.at).toLocaleString("ar")}</span>
            </div>
          ))}
        </div>
      )}

      {role === "customer" && !["closed", "rejected", "refunded", "exchanged"].includes(req.status) && (
        <button data-testid={`close-return-${req.id}`} onClick={() => update("closed")} className="mt-3 text-xs text-[#EF4444] hover:underline">إغلاق الطلب</button>
      )}

      {(role === "seller" || role === "admin") && (
        <div className="mt-3 pt-3 border-t flex flex-col sm:flex-row gap-2" style={{ borderColor: "var(--border-c)" }}>
          <input data-testid={`return-note-${req.id}`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="ملاحظة / سبب (إلزامي عند الرفض)"
            className="flex-1 rounded-xl bg-[var(--bg-secondary)] px-3 py-2 text-sm outline-none" />
          <select data-testid={`return-status-select-${req.id}`} value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-xl bg-[var(--bg-secondary)] px-3 py-2 text-sm outline-none">
            <option value="">تغيير الحالة</option>
            {options.map((st) => <option key={st} value={st}>{RETURN_STATUS[st]?.label || st}</option>)}
          </select>
          <button data-testid={`return-update-${req.id}`} onClick={() => update()} className="btn-amber !py-2 !px-4 text-sm">تحديث</button>
        </div>
      )}
    </div>
  );
}
